import { useEffect, useMemo, useState } from "react";
import { api, type APIResult } from "../../api/client";
import { useAppState } from "../../hooks/useAppState";
import { useExamples } from "../../hooks/useExamples";
import { formatJSON, uuid } from "../../lib/format";
import { JSONEditor } from "../JSONEditor";
import { PageHeader } from "../PageHeader";
import { ResponseCard } from "../ResponseCard";
import { SectionCard } from "../SectionCard";

// Tab para criar um job de otimização.
//
// Características didáticas:
//   - Carrega um exemplo (small-valid por default).
//   - Permite editar o JSON livremente antes do envio.
//   - Mostra/regenera Idempotency-Key (default sempre incluído).
//   - Quando a chamada é bem-sucedida, sugere navegar para Status do job.
type Props = {
  onNavigate: (tabId: string) => void;
};

export function TabCreateJob({ onNavigate }: Props) {
  const { examples } = useExamples();
  const { lastPayload, setLastPayload, setTrackedJobId, pushJobId } = useAppState();
  const [payload, setPayload] = useState<string>(lastPayload ?? "");
  const [idemKey, setIdemKey] = useState<string>(() => uuid());
  const [sendIdem, setSendIdem] = useState(true);
  const [result, setResult] = useState<APIResult<unknown> | null>(null);
  const [loading, setLoading] = useState(false);

  // Quando ainda não tem payload, carrega o "small-valid-request".
  useEffect(() => {
    if (payload) return;
    const target = examples.find((e) => e.name === "small-valid-request") ?? examples[0];
    if (!target) return;
    api.getExample(target.name).then((r) => {
      const text = r.ok ? formatJSON(r.data ?? r.raw) : r.raw;
      setPayload(text);
      setLastPayload(text);
    });
  }, [payload, examples, setLastPayload]);

  const newJob = useMemo(() => {
    const data = result?.data as { job_id?: string; status?: string } | null;
    return data && data.job_id ? data : null;
  }, [result]);

  async function send() {
    if (!payload.trim()) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(payload);
    } catch (err) {
      setResult({
        ok: false,
        status: 0,
        data: { error: "json_parse_error", message: String(err) },
        raw: String(err),
        latencyMs: 0,
      });
      return;
    }
    setLoading(true);
    setResult(null);
    const r = await api.createJob(parsed, sendIdem ? idemKey : undefined);
    setResult(r);
    setLoading(false);
    setLastPayload(payload);
    const data = r.data as { job_id?: string } | null;
    if (r.ok && data?.job_id) {
      setTrackedJobId(data.job_id);
      pushJobId(data.job_id);
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Criar job de otimização"
        subtitle="Envia o payload completo (depots + orders + drivers + vehicles + zones) para o solver."
        endpoint={{ method: "POST", path: "/v1/route-optimization/jobs" }}
        actions={
          <button type="button" className="ks-btn-primary" disabled={loading} onClick={send}>
            {loading ? "enviando..." : "Enviar"}
          </button>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <SectionCard
          title="Payload"
          description="Edição livre. Use a tab 'Exemplos prontos' para carregar templates rapidamente."
          actions={
            <>
              <button
                type="button"
                className="ks-btn-ghost text-xs"
                onClick={() => setPayload("")}
              >
                limpar
              </button>
              <button
                type="button"
                className="ks-btn-ghost text-xs"
                onClick={() => {
                  try {
                    setPayload(formatJSON(JSON.parse(payload)));
                  } catch {
                    /* mantém como está */
                  }
                }}
              >
                formatar
              </button>
            </>
          }
        >
          <JSONEditor value={payload} onChange={setPayload} rows={22} />
        </SectionCard>

        <div className="space-y-4">
          <SectionCard
            title="Headers"
            description="A API aceita Idempotency-Key (header) ou idempotency_key (body). Quando ambos são enviados, header vence."
          >
            <label className="flex items-center gap-2 text-sm text-ink-800">
              <input
                type="checkbox"
                className="size-4 rounded border-ink-300 accent-brand-600"
                checked={sendIdem}
                onChange={(e) => setSendIdem(e.target.checked)}
              />
              Enviar header <code className="ks-code">Idempotency-Key</code>
            </label>
            <div className="mt-3 space-y-1">
              <label className="ks-label">Valor</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  className="ks-input ks-code"
                  value={idemKey}
                  onChange={(e) => setIdemKey(e.target.value)}
                  disabled={!sendIdem}
                />
                <button
                  type="button"
                  className="ks-btn-secondary text-xs"
                  onClick={() => setIdemKey(uuid())}
                  disabled={!sendIdem}
                >
                  gerar
                </button>
              </div>
              <p className="text-[11px] text-ink-500">
                Reenviar com a mesma chave devolve o mesmo job (sem cobrar nova execução).
              </p>
            </div>
          </SectionCard>

          {newJob && (
            <SectionCard title="Job criado">
              <p className="text-sm text-ink-700">
                <span className="font-semibold">job_id:</span>{" "}
                <code className="ks-code">{newJob.job_id}</code>
              </p>
              <p className="mt-1 text-sm text-ink-700">
                <span className="font-semibold">status inicial:</span> {newJob.status}
              </p>
              <button
                type="button"
                className="ks-btn-primary mt-3 w-full"
                onClick={() => onNavigate("job-status")}
              >
                Acompanhar status em tempo real →
              </button>
            </SectionCard>
          )}

          <ResponseCard
            result={result}
            loading={loading}
            emptyHint="A resposta da API aparecerá aqui (201 ao criar, 200 quando idempotência hit)."
          />
        </div>
      </div>
    </div>
  );
}
